// ===================================================
// GAM Command Center — Slash Commands Extension
// Tiptap extension using @tiptap/suggestion + tippy.js
// ===================================================

import { Extension, type Editor, type Range } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { slashCommandItems, slashCommandCategories } from '../slash-command-items';
import { filterSlashItems, filterSlashCategories } from '../slash-command-filter';
import type { SlashCommandItem } from '../types';

/**
 * Bridge event to the host app's Sidebar (or any other listener) — the
 * shared editor package can't import app-level UI (Sidebar lives in
 * src/components/command-center), so it announces slash-menu state on
 * `window` instead, same pattern as the existing cc-insert-existing-field /
 * cc-definition-created bridge between FieldLibrary and CanvasEditor.
 */
export const GAM_SLASH_MENU_EVENT = 'gam:slash-menu';

export interface GamSlashMenuEventDetail {
  open: boolean;
  query?: string;
  categories?: { name: string; nameHe: string; items: SlashCommandItem[] }[];
  select?: (item: SlashCommandItem) => void;
  /** Caret rect in viewport coords — where the quick bar anchors itself. */
  anchor?: { top: number; left: number; bottom: number } | null;
  /** Doc range covering "/" + anything typed after it, for running a command. */
  range?: Range | null;
}

function dispatchSlashMenuEvent(detail: GamSlashMenuEventDetail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<GamSlashMenuEventDetail>(GAM_SLASH_MENU_EVENT, { detail }));
}

/**
 * An EXPLICIT dismissal — Escape, a pick, or a click outside — as opposed to
 * the `open: false` the extension itself emits from onExit.
 *
 * Those two are not the same thing and must never be conflated: the
 * suggestion plugin tears itself down on its own roughly 100ms after opening
 * (the original, still-unpinned bug), so any surface that treats that
 * teardown as a close vanishes the instant it appears. That was ed7949510
 * for the quick bar, which learned to ignore onExit and survived — while the
 * Sidebar's תוספים panel kept obeying it and stayed invisible for the two
 * months after, taking the full 27-plugin catalog with it.
 *
 * This event is the only close every surface may trust.
 */
export const GAM_SLASH_DISMISS_EVENT = 'gam:slash-dismiss';

export function dispatchSlashDismiss() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(GAM_SLASH_DISMISS_EVENT));
}

interface SuggestionCallbackProps {
  editor: Editor;
  range: Range;
  clientRect?: (() => DOMRect | null) | null;
  event?: KeyboardEvent;
  /** Only present in the top-level `command` option's own args — this IS
   * the selected SlashCommandItem there, not present on render-lifecycle
   * callbacks (onStart/onUpdate), which instead expose `.command(item)`
   * directly (see the `command` field below). */
  props?: SlashCommandItem;
  /** Present on render-lifecycle callbacks (onStart/onUpdate/onKeyDown) —
   * calling this with an item triggers the top-level `command` option
   * above with that item as `props`, which runs item.command(). */
  command?: (item: SlashCommandItem) => void;
  [key: string]: unknown;
}

export const SlashCommands = Extension.create({
  name: 'slashCommands',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        startOfLine: false,
        command: ({ editor, range, props }: SuggestionCallbackProps) => {
          (props as unknown as SlashCommandItem)?.command({ editor, range });
        },
        items: ({ query }: { query: string }) => filterSlashItems(query, slashCommandItems),
        // The extension no longer RENDERS anything — no ReactRenderer, no
        // tippy popup. It only reports "a slash is active, here, with this
        // query and range"; SlashQuickBar (rendered by TiptapEditor) and the
        // Sidebar's תוספים tab own the UI. That separation is deliberate:
        // the old popup was being destroyed on its own a couple of seconds
        // after opening, and a plugin teardown can no longer take the UI
        // down with it.
        render: () => {
          const announce = (props: SuggestionCallbackProps) => {
            const rect = props.clientRect?.();
            dispatchSlashMenuEvent({
              open: true,
              query: (props as { query?: string }).query ?? '',
              categories: filterSlashCategories(
                (props as { query?: string }).query ?? '',
                slashCommandCategories
              ),
              select: (item: SlashCommandItem) => props.command?.(item),
              anchor: rect ? { top: rect.top, left: rect.left, bottom: rect.bottom } : null,
              range: props.range ?? null,
            });
          };

          return {
            onStart: announce,
            onUpdate: announce,

            onKeyDown: (props: SuggestionCallbackProps) => {
              // Escape is handled by SlashQuickBar (it owns visibility now);
              // returning true here just stops the key reaching the document.
              if (props.event?.key === 'Escape') return true;
              return false;
            },

            onExit: () => {
              dispatchSlashMenuEvent({ open: false });
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
